/**
 * buildInlineCustomer — the PURE core of the "New customer" inline
 * create option inside the proposal/contract account picker
 * (NewProposalModal). The modal collects raw form state (name +
 * optional email); this helper validates it, normalizes whitespace and
 * email casing, guards against duplicating an existing account name,
 * and returns an input shaped for the EXISTING `createAccount` path in
 * `lib/db/accounts` — kept structural so this module stays store-free
 * and trivially testable.
 *
 * Pure by design: records in, result out. No store, no DOM, no clock —
 * the caller owns persistence (and the modal owns error display).
 */

// ---------------------------------------------------------------------------
// Input / output shapes
// ---------------------------------------------------------------------------

export interface InlineCustomerInput {
  /** Customer / company name (required). */
  name: string;
  /** Optional point-of-contact email; validated + lowercased when present. */
  email?: string;
  /** Signed-in operator, stamped as the account owner when provided. */
  ownerName?: string;
  /**
   * Names of accounts that already exist — creating an exact duplicate
   * (case/space-insensitive) is rejected so the operator picks the
   * existing record instead of forking the CRM.
   */
  existingNames?: string[];
}

/**
 * Ready-to-persist account input — structurally assignable to
 * `NewAccountInput` in `lib/db/accounts`.
 */
export interface InlineCustomerAccountInput {
  name: string;
  pocEmail?: string;
  ownerName?: string;
}

export type BuildInlineCustomerResult =
  | { ok: true; account: InlineCustomerAccountInput }
  | { ok: false; errors: string[] };

// ---------------------------------------------------------------------------
// Validation + build
// ---------------------------------------------------------------------------

/** Deliberately simple shape check: something@something.tld, no spaces. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Validate raw "New customer" form state and build the persistable
 * account input. Collects EVERY error (the modal lists them all at
 * once) instead of failing fast.
 */
export function buildInlineCustomer(
  input: InlineCustomerInput,
): BuildInlineCustomerResult {
  const errors: string[] = [];

  const name = input.name.trim();
  if (!name) {
    errors.push('Customer name is required.');
  } else if (
    (input.existingNames ?? []).some(
      (n) => normalizeName(n) === normalizeName(name),
    )
  ) {
    errors.push(
      `An account named "${name}" already exists — pick it from the list instead.`,
    );
  }

  const email = input.email?.trim().toLowerCase() || undefined;
  if (email && !EMAIL_RE.test(email)) {
    errors.push('Email looks invalid — use name@company.com.');
  }

  if (errors.length > 0) return { ok: false, errors };

  const account: InlineCustomerAccountInput = { name };
  if (email) account.pocEmail = email;
  const ownerName = input.ownerName?.trim();
  if (ownerName) account.ownerName = ownerName;
  return { ok: true, account };
}
